/**
 * @file src/components/molecules/DropDownSelectBox/TypeAheadDropDownSelectBox.tsx
 * @description Provides a reusable type-ahead dropdown component for selecting tokens. The actual filtering
 * is handled externally, and this component just passes typed search text upward. The parent re-filters
 * and passes updated `options` and `displayOptions` in.
 */

import useOnClickOrScrollOutside from '@/lib/hooks/useOnClickOrScrollOutside';
import { motion, AnimatePresence } from 'framer-motion';
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  CSSProperties,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
  UseFormClearErrors,
} from 'react-hook-form';
import { createPortal } from 'react-dom';
import FormInput, { FormInputProps } from '../Input/FormInput';
import { get } from 'lodash';
import APP_CONFIG from '@/config/app';
import { inputStyles } from '@/components/atoms/Input/Input';
import { logError, logInfo, logWarning } from '@/utils/logger';

const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;

export type OptionValue = number | string;

export interface DropDownSelectBoxOption<TValue extends OptionValue> {
  value: TValue;
  [key: string]: any;
}

interface TypeAheadDropDownProps<TValue extends OptionValue> {
  options: DropDownSelectBoxOption<TValue>[];
  displayOptions: React.ReactNode[];
  selectedDisplayOptions?: React.ReactNode[];
  onSelectItem?: (chosen: DropDownSelectBoxOption<TValue>) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  clearErrors?: UseFormClearErrors<any>;
  errors?: FieldErrors;
  label?: string;
  value?: OptionValue;
  intent?: string;
  name: string;
}

export interface DropDownRef {
  setSelectedOption: (value: OptionValue) => void;
}

const dropDownSpacingClasses = {
  dropDownPadding: 'p-sm',
  dropDownGap: 'gap-sm',
  dropDownSelectedOptionPadding: 'inset-0 mt-sm-eighthstep ml-sm-eighthstep',
};

const dropDownSizingClasses = {
  dropDownWidth: 'w-full',
  dropDownMaxHeight: 'max-h-96',
  dropDownMinWidth: 'min-w-52',
};

const dropDownColorClasses = {
  dropDownBackground: `bg-primary/10 backdrop-blur${MAIN_BACKDROP_BLUR}`,
  dropDownBorder: 'border border-solid border-primary/10',
};

const dropDownRoundednessClasses = {
  dropdownRounded: 'rounded-2xs',
};

const TypeAheadDropDownSelectBox = forwardRef<
  DropDownRef,
  FormInputProps<any> & TypeAheadDropDownProps<OptionValue>
>(function TypeAheadDropDownSelectBox(
  {
    onSelectItem,
    onSearchTermChange,
    register,
    setValue,
    clearErrors,
    errors,
    label,
    value,
    intent,
    name,
    options,
    displayOptions,
    selectedDisplayOptions,
    ...formInputProps
  },
  ref,
) {
  const [selectedValue, setSelectedValue] = useState<OptionValue | undefined>(
    value,
  );
  const [toggle, setToggle] = useState(false);
  const [toggleLock, setToggleLock] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [searchTerm, setSearchTerm] = useState('');

  const internalRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const error = get(errors, name as string);
  const hasError = !!error;

  useOnClickOrScrollOutside(
    internalRef,
    () => {
      if (hasError && clearErrors) {
        clearErrors(name);
      }
      setToggle(false);
    },
    portalRef,
  );

  useImperativeHandle(ref, () => ({
    setSelectedOption: (val: OptionValue) => {
      logInfo('setSelectedOption called', 'TypeAheadDropDownSelectBox', {
        val,
      });
      setSelectedValue(val);
    },
  }));

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleToggle = () => {
    if (toggleLock) return;
    if (hasError && clearErrors) {
      clearErrors(name);
    }
    setToggle(!toggle);
    setToggleLock(true);
    setTimeout(() => setToggleLock(false), 200);

    if (!toggle) {
      setSearchTerm('');
      onSearchTermChange?.('');
    }
  };

  const handleSelect = useCallback(
    (val: OptionValue, idx: number) => {
      if (val === undefined) {
        logError(
          new Error('Undefined value in handleSelect'),
          'TypeAheadDropDownSelectBox.handleSelect',
          { idx },
        );
        return;
      }
      setSelectedValue(val);
      onSelectItem?.({ value: val });
      setValue?.(name, val, { shouldValidate: true, shouldDirty: true });
      setToggle(false);
    },
    [onSelectItem, setValue, name],
  );

  useEffect(() => {
    if (!toggle) return;
    const el = internalRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        minWidth: rect.width,
        maxWidth: rect.width,
        position: 'absolute',
        zIndex: 50,
      });
    }
  }, [toggle]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value;
    setSearchTerm(typed);
    onSearchTermChange?.(typed);
  };

  const renderDropdown = useMemo(() => {
    if (typeof document === 'undefined') return null;
    if (options.length !== displayOptions.length) {
      logWarning(
        'Mismatch between options and displayOptions lengths',
        'TypeAheadDropDownSelectBox.renderDropdown',
        {
          optionsLength: options.length,
          displayOptionsLength: displayOptions.length,
        },
      );
    }

    return createPortal(
      <AnimatePresence>
        {toggle && (
          <motion.div
            initial={APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM.TOP.initial}
            animate={APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM.TOP.open}
            exit={APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM.TOP.closed}
            transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
            style={dropdownStyle}
            ref={portalRef}
            className={`
              relative
              flex
              flex-col
              ${dropDownColorClasses.dropDownBackground}
              ${dropDownRoundednessClasses.dropdownRounded}
              ${dropDownColorClasses.dropDownBorder}
              ${dropDownSizingClasses.dropDownMinWidth}
              ${dropDownSizingClasses.dropDownMaxHeight}
              ${dropDownSpacingClasses.dropDownGap}
              ${dropDownSpacingClasses.dropDownPadding}
            `}
          >
            <div className="sticky top-0 z-10">
              <FormInput
                size="input"
                name={`${name}-search`}
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                errors={errors}
              />
            </div>
            <div
              className={`overflow-y-auto grid
                ${dropDownRoundednessClasses.dropdownRounded}
                ${dropDownSpacingClasses.dropDownGap}`}
            >
              {options.map((opt, i) => (
                <div
                  key={`${opt.value}-${i}`}
                  onClick={() => handleSelect(opt.value, i)}
                  className={`flex items-center cursor-pointer`}
                >
                  {displayOptions[i]}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  }, [
    toggle,
    dropdownStyle,
    searchTerm,
    options,
    displayOptions,
    handleSelect,
    errors,
    name,
  ]);

  // Compute which option was chosen
  const selectedOptionIndex = options.findIndex(
    (opt) => opt.value === selectedValue,
  );
  const isValueSelected = selectedOptionIndex !== -1;

  return (
    <div className="relative w-full" ref={internalRef}>
      <FormInput
        size="dropdown"
        name={name}
        label={label}
        suffixIconName={toggle ? 'chevronUp' : 'chevronDown'}
        onIconClick={handleToggle}
        onClick={handleToggle}
        intent={toggle ? 'focused' : hasError ? 'error' : intent}
        {...formInputProps}
        value=""
        defaultValue=""
        errors={errors}
      />

      {isValueSelected && selectedDisplayOptions && (
        <div
          className={`${inputStyles({
            intent,
            size: 'dropdown',
            className: `absolute flex items-center pointer-events-none 
                ${dropDownSpacingClasses.dropDownSelectedOptionPadding}`,
          })}`}
        >
          {selectedDisplayOptions[selectedOptionIndex]}
        </div>
      )}

      {renderDropdown}
    </div>
  );
});

TypeAheadDropDownSelectBox.displayName = 'TypeAheadDropDownSelectBox';

export default TypeAheadDropDownSelectBox;
